import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import Header from "./components/redesign/Header";
import EntityCloud from "./components/redesign/EntityCloud";
import { MostRead } from "./components/redesign/Sections";
import AiDailyBrief from "./components/redesign/AiDailyBrief";
import TvSidebar from "./components/redesign/TvSidebar";
// TODO: re-enable when AI archive search is wired up
// import ArchiveSearchAi from "./components/redesign/ArchiveSearchAi";

import CategoryList from "./components/CategoryList";
import CategoryPage from "./components/CategoryPage";
import ClusterPage from "./components/ClusterPage";
import Impressum from "./components/Impressum";
import SearchPage from "./components/SearchPage";
import DailyBriefPage from "./components/DailyBriefPage";
import TvPage from "./components/TvPage";
import OpinionPage from "./components/OpinionPage";

import { fetchMostRead } from "./services/mostReadService";

// Below this breakpoint, the layout shows ONE pane at a time and surfaces
// arrow controls to switch between them. Matches the old mobile UX.
const PANES = ["left", "center", "right"];

function AppContent() {
  const location = useLocation();

  const currentCategory = (() => {
    const parts = location.pathname.split("/");
    if (parts[1] === "category" && parts[2]) {
      return decodeURIComponent(parts[2]);
    }
    return null;
  })();

  const isImpressum = location.pathname === "/impressum";
  const isTv = location.pathname === "/video";
  const isOpinion = location.pathname === "/kolumna";
  // Full-width pages render without the 3-column rails / pane switcher.
  const isFullWidth = isImpressum || isTv || isOpinion;

  // Most-read list comes from GA4 via the backend; falls back to a
  // placeholder list inside the service if the endpoint isn't ready.
  const [mostRead, setMostRead] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchMostRead().then((list) => {
      if (!cancelled) setMostRead(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Mobile pane switcher (sub-`lg` viewports). Reset to "center" whenever
  // the user navigates so that arriving on any page lands on its main feed.
  const [mobilePane, setMobilePane] = useState("center");
  useEffect(() => {
    setMobilePane("center");
  }, [location.pathname]);
  const paneIdx = PANES.indexOf(mobilePane);
  const goPrev = () =>
    setMobilePane(PANES[Math.max(0, paneIdx - 1)]);
  const goNext = () =>
    setMobilePane(PANES[Math.min(PANES.length - 1, paneIdx + 1)]);
  // Tailwind class fragment per pane: on `<lg` the active pane shows and the
  // others are hidden; at `lg+` everything is shown. Using literal class names
  // (not template-interpolated) so Tailwind's JIT scanner sees them.
  const paneShow = (pane, mode = "flex") => {
    const active = pane === mobilePane;
    if (mode === "block") return active ? "block lg:block" : "hidden lg:block";
    return active ? "flex lg:flex" : "hidden lg:flex";
  };

  return (
    <div className="min-h-screen bg-bg text-ink">
      <Header />

      {/* Mobile pane switcher — fixed top-right, hidden on desktop. */}
      {!isFullWidth && (
        <div className="fixed right-3 top-[64px] z-40 flex gap-2 lg:hidden">
          <button
            type="button"
            onClick={goPrev}
            disabled={paneIdx === 0}
            aria-label="Pane previous"
            className="grid h-9 w-9 place-items-center rounded-full bg-brand text-[14px] font-bold text-white shadow-md transition hover:bg-brand-2 disabled:opacity-30"
          >
            ◀
          </button>
          <span className="flex items-center gap-1 rounded-full bg-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-sub shadow-md">
            {mobilePane === "left" && "Persona"}
            {mobilePane === "center" && "Lajme"}
            {mobilePane === "right" && "AI"}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={paneIdx === PANES.length - 1}
            aria-label="Pane next"
            className="grid h-9 w-9 place-items-center rounded-full bg-brand text-[14px] font-bold text-white shadow-md transition hover:bg-brand-2 disabled:opacity-30"
          >
            ▶
          </button>
        </div>
      )}

      <div className="mx-auto max-w-site px-4 pb-14 lg:px-10">
        {isFullWidth ? (
          <main className="pt-8">
            <Routes>
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/video" element={<TvPage />} />
              <Route path="/kolumna" element={<OpinionPage />} />
            </Routes>
          </main>
        ) : (
          <div className="grid grid-cols-1 gap-6 pt-6 lg:grid-cols-[260px_1fr_260px] lg:gap-6 lg:pt-8 xl:grid-cols-[300px_1fr_300px] xl:gap-8">
            {/* LEFT */}
            <aside
              className={`${paneShow("left", "flex")} flex-col gap-6 lg:sticky lg:top-[84px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:pr-1`}
            >
              <EntityCloud
                title={
                  currentCategory
                    ? `Persona & entitete në ${currentCategory}`
                    : "Top personat & entitetet"
                }
                category={currentCategory}
              />
              <MostRead items={mostRead.slice(0, 5)} />
            </aside>

            {/* CENTER */}
            <main className={`${paneShow("center", "block")} min-w-0`}>
              <Routes>
                <Route path="/" element={<CategoryList />} />
                <Route path="/search" element={<SearchPage />} />
                <Route
                  path="/permbledhja-e-dites"
                  element={<DailyBriefPage />}
                />
                <Route
                  path="/category/:category"
                  element={<CategoryPage />}
                />
                <Route
                  path="/category/:category/cluster/:clusterId"
                  element={<ClusterPage />}
                />
              </Routes>
            </main>

            {/* RIGHT */}
            <aside
              className={`${paneShow("right", "flex")} flex-col gap-6 lg:sticky lg:top-[84px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:pl-1`}
            >
              <AiDailyBrief />
              <TvSidebar count={3} />
              {/* TODO: re-enable when AI archive search is wired up
              <ArchiveSearchAi />
              */}
            </aside>
          </div>
        )}
      </div>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-site flex-col items-center justify-between gap-2 px-4 py-6 text-[12px] text-sub md:flex-row lg:px-10">
          <span>© {new Date().getFullYear()} lajm.ai</span>
          <span>Lajme të kuruara nga AI · burimet janë pronë e mediave përkatëse.</span>
        </div>
      </footer>
    </div>
  );
}

function App() {
  useEffect(() => {
    document.body.classList.add("hydrated");
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
